import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useThemeMode } from '@/contexts/ThemeContext'

export interface TutorialStep {
  target: string
  title: string
  description: string
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  action?: () => void | Promise<void>
  offsetX?: number
  offsetY?: number
}

interface TutorialProps {
  steps: TutorialStep[]
  isOpen: boolean
  onComplete: () => void
  onSkip: () => void
}

type Rect = { x: number; y: number; width: number; height: number }

export default function Tutorial({ steps, isOpen, onComplete, onSkip }: TutorialProps) {
  const { colors, isDark } = useThemeMode()
  const [currentStep, setCurrentStep] = useState(0)
  const [targetRect, setTargetRect] = useState<Rect | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [nextDisabled, setNextDisabled] = useState(false)
  const stepActionExecutedRef = useRef<Set<number>>(new Set())
  const lastStepRef = useRef<number>(-1)

  const step = steps[currentStep]
  const isLastStep = currentStep === steps.length - 1
  const isFirstStep = currentStep === 0

  const updatePositions = useCallback(() => {
    if (!step?.target) return
    const elements = Array.from(
      document.querySelectorAll(step.target)
    ) as HTMLElement[]
    if (elements.length === 0) return
    // Compute union of all matching elements' rects
    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY
    for (const el of elements) {
      const r = el.getBoundingClientRect()
      minX = Math.min(minX, r.left)
      minY = Math.min(minY, r.top)
      maxX = Math.max(maxX, r.right)
      maxY = Math.max(maxY, r.bottom)
    }
    const rect: Rect = { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) }
    setTargetRect(rect)

    const padding = 20
    const tooltipWidth = 320
    const tooltipHeight = 200
    let x = 0
    let y = 0

    const right = rect.x + rect.width
    const bottom = rect.y + rect.height

    switch (step.placement) {
      case 'top':
        x = rect.x + rect.width / 2 - tooltipWidth / 2
        y = rect.y - tooltipHeight - padding
        break
      case 'bottom':
        x = rect.x + rect.width / 2 - tooltipWidth / 2
        y = bottom + padding
        break
      case 'left':
        x = rect.x - tooltipWidth - padding
        y = rect.y + rect.height / 2 - tooltipHeight / 2
        break
      case 'right':
        x = right + padding
        y = rect.y + rect.height / 2 - tooltipHeight / 2
        break
      case 'center':
      default:
        x = window.innerWidth / 2 - tooltipWidth / 2
        y = window.innerHeight / 2 - tooltipHeight / 2
        break
    }

    // Apply optional per-step offsets
    if (typeof step.offsetX === 'number') x += step.offsetX
    if (typeof step.offsetY === 'number') y += step.offsetY

    x = Math.max(padding, Math.min(x, window.innerWidth - tooltipWidth - padding))
    y = Math.max(padding, Math.min(y, window.innerHeight - tooltipHeight - padding))
    setTooltipPosition({ x, y })
  }, [step])

  useEffect(() => {
    if (!isOpen) return

    // If user went backward, allow actions to re-run by clearing the set
    if (currentStep < lastStepRef.current) {
      console.log('[Tutorial] User went backward, clearing action execution tracking')
      stepActionExecutedRef.current.clear()
    }
    lastStepRef.current = currentStep

    // Clear any lingering tutorial adornments before applying this step
    try {
      document
        .querySelectorAll('.tutorial-pulse')
        .forEach((el) => el.classList.remove('tutorial-pulse'))
      document
        .querySelectorAll('.tutorial-target')
        .forEach((el) => el.classList.remove('tutorial-target'))
    } catch {}

    // Run step action first so DOM changes (e.g., opening menus) are present
    // Only run once per step using a Set to track executed steps
    if (step?.action && !stepActionExecutedRef.current.has(currentStep)) {
      stepActionExecutedRef.current.add(currentStep)
      console.log('[Tutorial] Running step action for:', step.title)
      try {
        const result = step.action()
        // If the action returns a Promise, handle it
        if (result instanceof Promise) {
          console.log('[Tutorial] Action is async, awaiting...')
          result.catch((error) => {
            console.error('[Tutorial] Error in async step action:', error)
          })
        }
      } catch (error) {
        console.error('[Tutorial] Error running step action:', error)
      }
    }
    // Recompute positions on next frame and shortly after to capture async UI
    requestAnimationFrame(() => updatePositions())
    setTimeout(() => updatePositions(), 150)
    window.addEventListener('resize', updatePositions)
    window.addEventListener('scroll', updatePositions, true)
    return () => {
      window.removeEventListener('resize', updatePositions)
      window.removeEventListener('scroll', updatePositions, true)
    }
  }, [isOpen, currentStep, step, updatePositions])

  // Cleanup when closing/unmounting tutorial
  useEffect(() => {
    if (!isOpen) {
      try {
        document
          .querySelectorAll('.tutorial-pulse')
          .forEach((el) => el.classList.remove('tutorial-pulse'))
        document
          .querySelectorAll('.tutorial-target')
          .forEach((el) => el.classList.remove('tutorial-target'))
      } catch {}
      // Reset the action execution tracker and current step
      stepActionExecutedRef.current.clear()
      lastStepRef.current = -1
      setCurrentStep(0)
    }
    return () => {
      try {
        document
          .querySelectorAll('.tutorial-pulse')
          .forEach((el) => el.classList.remove('tutorial-pulse'))
        document
          .querySelectorAll('.tutorial-target')
          .forEach((el) => el.classList.remove('tutorial-target'))
      } catch {}
    }
  }, [isOpen])

  const handleNext = () => {
    if (nextDisabled) return
    if (isLastStep) onComplete()
    else setCurrentStep((prev) => prev + 1)
  }
  const handlePrevious = () => {
    if (!isFirstStep) setCurrentStep((prev) => prev - 1)
  }
  const handleSkip = () => onSkip()

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log('[Tutorial] Key pressed:', e.key)
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      console.log('[Tutorial] Target tag:', tag)
      const isInputLike =
        tag === 'input' ||
        tag === 'textarea' ||
        (target as HTMLElement | null)?.isContentEditable === true
      console.log('[Tutorial] Is input-like:', isInputLike)

      // If ESC is pressed while in textarea/input, blur it instead of closing tutorial
      if (e.key === 'Escape' && isInputLike) {
        console.log('[Tutorial] ESC pressed in input/textarea, blurring...')
        e.preventDefault()
        ;(target as HTMLElement).blur()
        console.log('[Tutorial] Blurred, active element is now:', document.activeElement?.tagName)
        return
      }

      // Ignore tutorial hotkeys when typing, or while Next is disabled
      if (isInputLike || nextDisabled) {
        console.log('[Tutorial] Ignoring key because isInputLike:', isInputLike, 'or nextDisabled:', nextDisabled)
        return
      }

      console.log('[Tutorial] Processing tutorial hotkey:', e.key)
      if (e.key === 'Escape') handleSkip()
      else if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext()
      else if (e.key === 'ArrowLeft') handlePrevious()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, currentStep, nextDisabled])

  // Removed external 'tutorial-next' auto-advance listener to prevent auto advancing

  // Allow external control of Next button enable/disable
  useEffect(() => {
    const disable = () => setNextDisabled(true)
    const enable = () => setNextDisabled(false)
    window.addEventListener('tutorial-disable-next', disable as EventListener)
    window.addEventListener('tutorial-enable-next', enable as EventListener)
    return () => {
      window.removeEventListener('tutorial-disable-next', disable as EventListener)
      window.removeEventListener('tutorial-enable-next', enable as EventListener)
    }
  }, [])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay with spotlight */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 99999,
              pointerEvents: 'none',
            }}
          >
            <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
              <defs>
                <mask id="spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  {targetRect && (
                    <motion.rect
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                      x={targetRect.x - 8}
                      y={targetRect.y - 8}
                      width={Math.max(0, targetRect.width + 16)}
                      height={Math.max(0, targetRect.height + 16)}
                      rx="8"
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill={isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.5)'}
                mask="url(#spotlight-mask)"
              />
            </svg>
            {targetRect && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  left: targetRect.x - 8,
                  top: targetRect.y - 8,
                  width: Math.max(0, targetRect.width + 16),
                  height: Math.max(0, targetRect.height + 16),
                  border: `2px solid ${colors.visualAccent1}`,
                  borderRadius: '8px',
                  boxShadow: `0 0 0 4px ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  pointerEvents: 'none',
                }}
              >
                <motion.div
                  animate={{ scale: [1, 1.04, 1], opacity: [0.6, 0.85, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'absolute',
                    inset: -4,
                    border: `2px solid ${colors.visualAccent1}`,
                    borderRadius: '10px',
                  }}
                />
              </motion.div>
            )}
          </motion.div>

          {/* Tooltip */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              zIndex: 100000,
              width: '320px',
              maxWidth: 'calc(100vw - 40px)',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                position: 'relative',
                backgroundColor: colors.primaryBg,
                borderRadius: '10px',
                padding: '20px',
                boxShadow: isDark
                  ? '0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)'
                  : '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.06)',
                border: `1px solid ${colors.primaryBorder}`,
              }}
            >
              <button
                onClick={handleSkip}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '2px 6px',
                  background: 'transparent',
                  border: `1px solid ${colors.primaryBorder}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: colors.secondaryText,
                }}
                title="Close"
              >
                ×
              </button>

              <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', paddingRight: '24px' }}>
                {steps.map((_, index) => (
                  <motion.div
                    key={index}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: index * 0.05 }}
                    style={{
                      flex: 1,
                      height: '3px',
                      backgroundColor:
                        index <= currentStep ? colors.visualAccent1 : colors.primaryBorder,
                      borderRadius: '2px',
                      transformOrigin: 'left',
                    }}
                  />
                ))}
              </div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}>
                <h3
                  style={{
                    margin: '0 0 10px 0',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: colors.primaryText,
                  }}
                >
                  {step.title}
                </h3>
                <p
                  style={{
                    margin: '0 0 18px 0',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    color: colors.secondaryText,
                  }}
                >
                  {step.description}
                </p>
              </motion.div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={handleSkip}
                  style={{
                    padding: '6px 10px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: colors.secondaryText,
                    fontWeight: 500,
                    borderRadius: '6px',
                  }}
                >
                  Skip
                </button>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {!isFirstStep && (
                    <motion.button
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={handlePrevious}
                      style={{
                        padding: '6px 10px',
                        background: colors.secondaryBg,
                        border: `1px solid ${colors.primaryBorder}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: colors.primaryText,
                      }}
                    >
                      ← Back
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleNext}
                    disabled={nextDisabled}
                    data-tutorial-next-button
                    style={{
                      padding: '6px 12px',
                      background: nextDisabled ? colors.primaryBorder : colors.visualAccent1,
                      border: 'none',
                      borderRadius: '6px',
                      cursor: nextDisabled ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      color: '#ffffff',
                      fontWeight: 600,
                    }}
                  >
                    {isLastStep ? 'Finish' : 'Next →'}
                  </motion.button>
                </div>
              </div>

              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: `1px solid ${colors.primaryBorder}`,
                  textAlign: 'center',
                  fontSize: '11px',
                  color: colors.tertiaryText,
                }}
              >
                Step {currentStep + 1} of {steps.length}
              </div>

              {step.placement !== 'center' && targetRect && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    backgroundColor: colors.primaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    transform: 'rotate(45deg)',
                    ...(step.placement === 'top' && {
                      bottom: '-6px',
                      left: '50%',
                      marginLeft: '-6px',
                      borderTop: 'none',
                      borderLeft: 'none',
                    }),
                    ...(step.placement === 'bottom' && {
                      top: '-6px',
                      left: '50%',
                      marginLeft: '-6px',
                      borderBottom: 'none',
                      borderRight: 'none',
                    }),
                    ...(step.placement === 'left' && {
                      right: '-6px',
                      top: '50%',
                      marginTop: '-6px',
                      borderTop: 'none',
                      borderRight: 'none',
                    }),
                    ...(step.placement === 'right' && {
                      left: '-6px',
                      top: '50%',
                      marginTop: '-6px',
                      borderBottom: 'none',
                      borderLeft: 'none',
                    }),
                  }}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}


